import { Card, Stack, Tooltip, Typography, Box } from '@mui/material';
import ABTestLabelBox from 'domains/campaigns/components/ABTestLabelBox';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WinnerMedalIcon from 'domains/core/components/SvgIcons/WinnerMedalIcon';
import { themeColors } from 'colors';

type ABTestInsightCardProps = {
    label: string;
    title: string;
    subtitle: string;
    deliveryRate: string;
    openRate: string;
    clickRate: string;
    isSelected: boolean;
    winner: boolean;
    isTestCompleted: boolean; // New prop to indicate if test has ended
    onSelect: () => void;
};

const ABTestInsightCard = ({
    label,
    title,
    subtitle,
    deliveryRate,
    openRate,
    clickRate,
    isSelected,
    winner,
    isTestCompleted,
    onSelect,
}: ABTestInsightCardProps) => (
    <Card
        onClick={isTestCompleted ? undefined : onSelect}
        sx={{
            border: isSelected ? `2px solid ${themeColors.success.main}` : `1px solid ${themeColors.other.divider}`,
            cursor: isTestCompleted ? 'default' : 'pointer',
            padding: 2,
            position: 'relative',
            width: '440px',
            '&:hover': {
                borderColor: isTestCompleted
                    ? isSelected
                        ? themeColors.success.main
                        : themeColors.other.divider
                    : isSelected
                    ? themeColors.success.main
                    : themeColors.text.disabled,
            },
        }}
    >
        <Stack spacing={2}>
            {/* Header with label, title and icon */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ flexShrink: 0 }}>
                        <ABTestLabelBox label={label} size="medium" />
                    </Box>
                    <Stack sx={{ minWidth: 0, flex: 1 }}>
                        <Tooltip title={title} arrow>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {title}
                            </Typography>
                        </Tooltip>
                        <Tooltip title={subtitle} arrow>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {subtitle}
                            </Typography>
                        </Tooltip>
                    </Stack>
                </Stack>
                {/* Show winner medal if test completed, otherwise show checkmark for selection */}
                <Stack sx={{ flexShrink: 0 }}>
                    {isTestCompleted ? (
                        winner && (
                            <WinnerMedalIcon
                                sx={{
                                    fontSize: 32,
                                    color: themeColors.success.main,
                                }}
                            />
                        )
                    ) : isSelected ? (
                        <CheckCircleIcon sx={{ color: themeColors.success.main, fontSize: 28 }} />
                    ) : (
                        <CheckCircleOutlineIcon sx={{ color: themeColors.text.disabled, fontSize: 28 }} />
                    )}
                </Stack>
            </Stack>

            {/* Metrics - Values Only */}
            <Stack spacing={1} sx={{ borderTop: `1px solid ${themeColors.other.divider}`, pt: 2 }}>
                <Stack direction="row" justifyContent="center" sx={{ height: '40px', alignItems: 'center' }}>
                    <Typography variant="body1">{deliveryRate}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="center" sx={{ height: '40px', alignItems: 'center' }}>
                    <Typography variant="body1">{openRate}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="center" sx={{ height: '40px', alignItems: 'center' }}>
                    <Typography variant="body1">{clickRate}</Typography>
                </Stack>
            </Stack>
        </Stack>
    </Card>
);

export default ABTestInsightCard;
